import {
  Analytics,
  ArrowsVertical,
  Building,
  Calendar,
  Catalog,
  Checkmark,
  CheckmarkOutline,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleDash,
  Close,
  CloudUpload,
  CurrencyDollar,
  Dashboard,
  DocumentMultiple_01,
  Education,
  Menu,
  Portfolio,
  Purchase,
  Receipt,
  Renew,
  Search,
  Security,
  Subtract,
  SubtractAlt,
  Task,
  UserMultiple,
  WarningAlt,
} from "@carbon/icons-react";
import type { CarbonIconProps, CarbonIconType } from "@carbon/icons-react";
import type { ReactElement } from "react";
import { cn } from "@/shared/utils/cn";
import type { IconographyAsset } from "@/types/clm-web";

export type IconName =
  | "dashboard"
  | "admin"
  | "member"
  | "directory"
  | "jobs"
  | "ucc"
  | "events"
  | "courses"
  | "compliance"
  | "financial"
  | "documents"
  | "search"
  | "renew"
  | "reporting"
  | "payment"
  | "receipt"
  | "upload"
  | "menu"
  | "loading"
  | "sort"
  | "chevron-left"
  | "chevron-right"
  | "chevron-up"
  | "chevron-down"
  | "close"
  | "option-selected"
  | "status-success"
  | "status-warning"
  | "status-inactive"
  | "checkbox-checked"
  | "checkbox-indeterminate";

export interface IconMapEntry extends IconographyAsset {
  icon: CarbonIconType;
}

export const ICON_MAP = {
  dashboard: {
    semanticUse: "Dashboard navigation",
    sourceType: "carbon",
    sourcePathOrName: "Dashboard",
    accessibilityLabel: "Dashboard",
    approvalStatus: "approved",
    icon: Dashboard,
  },
  admin: {
    semanticUse: "Admin navigation",
    sourceType: "carbon",
    sourcePathOrName: "Building",
    accessibilityLabel: "Admin",
    approvalStatus: "approved",
    icon: Building,
  },
  member: {
    semanticUse: "Users/member navigation",
    sourceType: "carbon",
    sourcePathOrName: "UserMultiple",
    accessibilityLabel: "Members",
    approvalStatus: "approved",
    icon: UserMultiple,
  },
  directory: {
    semanticUse: "Industry resource directory navigation",
    sourceType: "carbon",
    sourcePathOrName: "Catalog",
    accessibilityLabel: "Industry Resource Directory",
    approvalStatus: "approved",
    icon: Catalog,
  },
  jobs: {
    semanticUse: "Jobs navigation",
    sourceType: "carbon",
    sourcePathOrName: "Portfolio",
    accessibilityLabel: "Jobs",
    approvalStatus: "approved",
    icon: Portfolio,
  },
  ucc: {
    semanticUse: "Licensing/UCC navigation",
    sourceType: "carbon",
    sourcePathOrName: "Task",
    accessibilityLabel: "UCC",
    approvalStatus: "needs-product-review",
    icon: Task,
  },
  events: {
    semanticUse: "Calendar/events navigation",
    sourceType: "carbon",
    sourcePathOrName: "Calendar",
    accessibilityLabel: "Events",
    approvalStatus: "approved",
    icon: Calendar,
  },
  courses: {
    semanticUse: "Courses navigation",
    sourceType: "carbon",
    sourcePathOrName: "Education",
    accessibilityLabel: "Courses",
    approvalStatus: "approved",
    icon: Education,
  },
  compliance: {
    semanticUse: "Compliance/status navigation",
    sourceType: "carbon",
    sourcePathOrName: "Security",
    accessibilityLabel: "Compliance",
    approvalStatus: "needs-product-review",
    icon: Security,
  },
  financial: {
    semanticUse: "Payments/financial navigation",
    sourceType: "carbon",
    sourcePathOrName: "CurrencyDollar",
    accessibilityLabel: "Financial",
    approvalStatus: "needs-product-review",
    icon: CurrencyDollar,
  },
  documents: {
    semanticUse: "Documents navigation",
    sourceType: "carbon",
    sourcePathOrName: "DocumentMultiple_01",
    accessibilityLabel: "Documents",
    approvalStatus: "needs-product-review",
    icon: DocumentMultiple_01,
  },
  search: {
    semanticUse: "Search",
    sourceType: "carbon",
    sourcePathOrName: "Search",
    accessibilityLabel: "Search",
    approvalStatus: "approved",
    icon: Search,
  },
  renew: {
    semanticUse: "Renew/status action",
    sourceType: "carbon",
    sourcePathOrName: "Renew",
    accessibilityLabel: "Renew",
    approvalStatus: "approved",
    icon: Renew,
  },
  reporting: {
    semanticUse: "Reporting/charts",
    sourceType: "carbon",
    sourcePathOrName: "Analytics",
    accessibilityLabel: "Reporting",
    approvalStatus: "approved",
    icon: Analytics,
  },
  payment: {
    semanticUse: "Payment action",
    sourceType: "carbon",
    sourcePathOrName: "Purchase",
    accessibilityLabel: "Payment",
    approvalStatus: "needs-product-review",
    icon: Purchase,
  },
  receipt: {
    semanticUse: "Receipt action",
    sourceType: "carbon",
    sourcePathOrName: "Receipt",
    accessibilityLabel: "Receipt",
    approvalStatus: "needs-product-review",
    icon: Receipt,
  },
  upload: {
    semanticUse: "Document upload action",
    sourceType: "carbon",
    sourcePathOrName: "CloudUpload",
    accessibilityLabel: "Upload",
    approvalStatus: "needs-product-review",
    icon: CloudUpload,
  },
  menu: {
    semanticUse: "Navigation menu toggle",
    sourceType: "carbon",
    sourcePathOrName: "Menu",
    accessibilityLabel: "Menu",
    approvalStatus: "approved",
    icon: Menu,
  },
  loading: {
    semanticUse: "Loading indicator",
    sourceType: "carbon",
    sourcePathOrName: "CircleDash",
    accessibilityLabel: "Loading",
    approvalStatus: "approved",
    icon: CircleDash,
  },
  sort: {
    semanticUse: "Sortable column header toggle",
    sourceType: "carbon",
    sourcePathOrName: "ArrowsVertical",
    accessibilityLabel: "Sort",
    approvalStatus: "needs-product-review",
    icon: ArrowsVertical,
  },
  "chevron-left": {
    semanticUse: "Previous page/step navigation",
    sourceType: "carbon",
    sourcePathOrName: "ChevronLeft",
    accessibilityLabel: "Previous",
    approvalStatus: "needs-product-review",
    icon: ChevronLeft,
  },
  "chevron-right": {
    semanticUse: "Next page/step navigation",
    sourceType: "carbon",
    sourcePathOrName: "ChevronRight",
    accessibilityLabel: "Next",
    approvalStatus: "needs-product-review",
    icon: ChevronRight,
  },
  "chevron-up": {
    semanticUse: "Scroll/collapse upward affordance",
    sourceType: "carbon",
    sourcePathOrName: "ChevronUp",
    accessibilityLabel: "Scroll up",
    approvalStatus: "needs-product-review",
    icon: ChevronUp,
  },
  "chevron-down": {
    semanticUse: "Open/expand downward affordance",
    sourceType: "carbon",
    sourcePathOrName: "ChevronDown",
    accessibilityLabel: "Open",
    approvalStatus: "needs-product-review",
    icon: ChevronDown,
  },
  close: {
    semanticUse: "Dismiss/close action",
    sourceType: "carbon",
    sourcePathOrName: "Close",
    accessibilityLabel: "Close",
    approvalStatus: "needs-product-review",
    icon: Close,
  },
  "option-selected": {
    semanticUse: "Selected option indicator",
    sourceType: "carbon",
    sourcePathOrName: "Checkmark",
    accessibilityLabel: "Selected",
    approvalStatus: "needs-product-review",
    icon: Checkmark,
  },
  "status-success": {
    semanticUse: "Success status",
    sourceType: "carbon",
    sourcePathOrName: "CheckmarkOutline",
    accessibilityLabel: "Success",
    approvalStatus: "needs-product-review",
    icon: CheckmarkOutline,
  },
  "status-warning": {
    semanticUse: "Warning status",
    sourceType: "carbon",
    sourcePathOrName: "WarningAlt",
    accessibilityLabel: "Warning",
    approvalStatus: "needs-product-review",
    icon: WarningAlt,
  },
  "status-inactive": {
    semanticUse: "Inactive/deactivated status",
    sourceType: "carbon",
    sourcePathOrName: "SubtractAlt",
    accessibilityLabel: "Inactive",
    approvalStatus: "needs-product-review",
    icon: SubtractAlt,
  },
  "checkbox-checked": {
    semanticUse: "Checkbox checked mark",
    sourceType: "carbon",
    sourcePathOrName: "Checkmark",
    accessibilityLabel: "Checked",
    approvalStatus: "needs-product-review",
    icon: Checkmark,
  },
  "checkbox-indeterminate": {
    semanticUse: "Checkbox indeterminate mark",
    sourceType: "carbon",
    sourcePathOrName: "Subtract",
    accessibilityLabel: "Partially checked",
    approvalStatus: "needs-product-review",
    icon: Subtract,
  },
} satisfies Record<IconName, IconMapEntry>;

// "size" is omitted because the wrapper sizes via className (h-5 w-5 default),
// so Carbon's size prop would be a visually dead no-op for callers.
export interface ClmIconProps extends Omit<CarbonIconProps, "aria-label" | "size"> {
  name: IconName;
  label?: string;
  decorative?: boolean;
}

export function ClmIcon({
  name,
  label,
  decorative = false,
  className,
  ...props
}: ClmIconProps): ReactElement {
  const entry = ICON_MAP[name];
  const Icon = entry.icon;
  const accessibleLabel = label ?? entry.accessibilityLabel;

  if (decorative) {
    return (
      <Icon aria-hidden="true" focusable="false" className={cn("h-5 w-5", className)} {...props} />
    );
  }

  return (
    <Icon role="img" aria-label={accessibleLabel} className={cn("h-5 w-5", className)} {...props} />
  );
}

export function getIconMapEntry(name: IconName): IconMapEntry {
  return ICON_MAP[name];
}

export function resolveIconName(navIcon: string): IconName {
  // Own-property check: `in` would match inherited Object.prototype keys (e.g. "toString").
  return Object.hasOwn(ICON_MAP, navIcon) ? (navIcon as IconName) : "dashboard";
}
